import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { Avatar, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import useCopyToClipboard from "@/hooks/useCopyToClipboard";
import { useTranslation } from "@/hooks/useTranslation";
import { AdminUserDTO } from "@shaarp/common";
import { ColumnDef } from "@tanstack/react-table";
import { CheckIcon, CopyIcon } from "lucide-react";
import { useState } from "react";
import DataTableCellAdminUserActions from "./data-table-cell-admin-user-actions";
import { DialogAdminUserTeams } from "./dialog-admin-user-teams";

export const dataTableAdminUsersColumns = (onRefresh: () => void) => {
  const { t } = useTranslation();

  return [
    {
      accessorKey: "avatar",
      header: "",
      cell: ({ row }) => {
        const user = row.original;
        return (
          <div className="flex justify-center">
            <Avatar className="w-12 h-12">
              <AvatarImage src={user.imageUrl} alt={user.fullName} />
            </Avatar>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: "Prénom",
      accessorFn: (user) => user.firstName,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminUsers.columns.firstName")}
        />
      ),
      cell: ({ row }) => {
        const user = row.original;
        return <span>{user.firstName}</span>;
      },
    },
    {
      id: "Nom",
      accessorFn: (user) => user.lastName,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminUsers.columns.lastName")}
        />
      ),
      cell: ({ row }) => {
        const user = row.original;
        return <span>{user.lastName}</span>;
      },
    },
    {
      id: "Poste",
      accessorFn: (user) => user.jobTitle,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminUsers.columns.jobTitle")}
        />
      ),
    },
    {
      id: "Email",
      accessorFn: (user) => user.email,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminUsers.columns.email")}
        />
      ),
      cell: ({ row }) => {
        const user = row.original;
        return <span className="font-medium">{user.email}</span>;
      },
    },
    {
      id: "Téléphone",
      accessorFn: (user) => user.phoneNumber,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminUsers.columns.phone")}
        />
      ),
    },
    {
      id: "Rejoint le",
      accessorFn: (user) => user.createdAt,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminUsers.columns.joinedAt")}
        />
      ),
      cell: ({ row }) => {
        const date = new Date(row.original.createdAt);
        return (
          <span>
            {date.toLocaleDateString("fr-FR")}{" "}
            {date.toLocaleTimeString("fr-FR", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        );
      },
      sortDescFirst: true,
    },
    {
      id: "Equipes",
      accessorFn: (user) => user.teamsCount,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminUsers.columns.teams")}
        />
      ),
      cell: ({ row }) => {
        const user = row.original;
        const [open, setOpen] = useState(false);

        return (
          <>
            <Tooltip>
              <TooltipTrigger>
                <Badge variant="outline" onClick={() => setOpen(true)}>
                  {user.teamsCount !== null ? user.teamsCount : "?"}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                {user.teamsCount !== null
                  ? t("admin.dataTableAdminUsers.tooltips.teamsCount", {
                      count: user.teamsCount,
                    })
                  : t("admin.dataTableAdminUsers.dialogs.teams.empty")}
              </TooltipContent>
            </Tooltip>

            {open && (
              <DialogAdminUserTeams
                user={user}
                open={open}
                onClose={() => setOpen(false)}
              />
            )}
          </>
        );
      },
    },
    {
      id: "Lien parrainage",
      accessorFn: (user) => user.referralCode,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminUsers.columns.referralLink")}
        />
      ),
      cell: ({ row }) => {
        const user = row.original;
        const [, copy] = useCopyToClipboard();
        const [copied, setCopied] = useState(false);

        const referralUrl = `${import.meta.env.VITE_WEBSITE_BASE_URL}/r/${
          user.referralCode
        }`;

        const handleCopy = async () => {
          const success = await copy(referralUrl);
          if (success) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }
        };

        return (
          <div className="flex gap-2 items-center">
            <code className="px-2 py-1 text-xs rounded bg-muted">
              {user.referralCode}
            </code>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-8 h-8"
                  onClick={handleCopy}
                >
                  {copied ? (
                    <CheckIcon className="w-4 h-4 text-green-500" />
                  ) : (
                    <CopyIcon className="w-4 h-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {copied
                  ? t("admin.dataTableAdminUsers.tooltips.referralLinkCopied")
                  : t("admin.dataTableAdminUsers.tooltips.copyReferralLink")}
              </TooltipContent>
            </Tooltip>
          </div>
        );
      },
      enableSorting: false,
    },
    {
      id: "Advisor",
      accessorFn: (user) => user.advisorLicence,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminUsers.columns.advisor")}
        />
      ),
      cell: ({ row }) => {
        const user = row.original;
        if (!user.advisorLicence) {
          return null;
        }

        return (
          <div className="flex gap-2">
            <Badge variant="success">
              {user.advisorLicence === "professional"
                ? t("admin.dataTableAdminUsers.badges.hasProLicence")
                : t("admin.dataTableAdminUsers.badges.hasBasicLicence")}
            </Badge>
            <Badge variant="outline">
              {user.hasTeamsValid
                ? t("admin.dataTableAdminUsers.badges.subscribed")
                : t("admin.dataTableAdminUsers.badges.notSubscribed")}
            </Badge>
            {user.advisorMonthlyDiscount && (
              <Badge variant="outline">M:{user.advisorMonthlyDiscount}%</Badge>
            )}
            {user.advisorAnnualDiscount && (
              <Badge variant="outline">A:{user.advisorAnnualDiscount}%</Badge>
            )}
          </div>
        );
      },
    },
    {
      id: "Referral",
      accessorFn: (user) =>
        user.referralMonthlyDiscount || user.referralAnnualDiscount,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminUsers.columns.referral")}
        />
      ),
      cell: ({ row }) => {
        const user = row.original;
        const [, copy] = useCopyToClipboard();
        const [copiedMonthly, setCopiedMonthly] = useState(false);
        const [copiedAnnual, setCopiedAnnual] = useState(false);

        const hasDiscounts =
          user.referralMonthlyDiscount || user.referralAnnualDiscount;

        if (!hasDiscounts) {
          return null;
        }

        const handleCopyMonthly = async () => {
          if (user.referralMonthlyPromotionCode) {
            const success = await copy(user.referralMonthlyPromotionCode);
            if (success) {
              setCopiedMonthly(true);
              setTimeout(() => setCopiedMonthly(false), 2000);
            }
          }
        };

        const handleCopyAnnual = async () => {
          if (user.referralAnnualPromotionCode) {
            const success = await copy(user.referralAnnualPromotionCode);
            if (success) {
              setCopiedAnnual(true);
              setTimeout(() => setCopiedAnnual(false), 2000);
            }
          }
        };

        return (
          <div className="flex flex-col gap-2">
            {user.referralMonthlyDiscount && (
              <div className="flex gap-2 items-center">
                <Badge variant="outline">
                  M:{user.referralMonthlyDiscount}%
                </Badge>
                {user.referralMonthlyPromotionCode && (
                  <>
                    <code className="px-2 py-1 text-xs rounded bg-muted">
                      {user.referralMonthlyPromotionCode}
                    </code>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-6 h-6"
                          onClick={handleCopyMonthly}
                        >
                          {copiedMonthly ? (
                            <CheckIcon className="w-3 h-3 text-green-500" />
                          ) : (
                            <CopyIcon className="w-3 h-3" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {copiedMonthly
                          ? t(
                              "admin.dataTableAdminUsers.tooltips.promoCodeCopied"
                            )
                          : t(
                              "admin.dataTableAdminUsers.tooltips.copyPromoCode"
                            )}
                      </TooltipContent>
                    </Tooltip>
                  </>
                )}
              </div>
            )}
            {user.referralAnnualDiscount && (
              <div className="flex gap-2 items-center">
                <Badge variant="outline">
                  A:{user.referralAnnualDiscount}%
                </Badge>
                {user.referralAnnualPromotionCode && (
                  <>
                    <code className="px-2 py-1 text-xs rounded bg-muted">
                      {user.referralAnnualPromotionCode}
                    </code>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-6 h-6"
                          onClick={handleCopyAnnual}
                        >
                          {copiedAnnual ? (
                            <CheckIcon className="w-3 h-3 text-green-500" />
                          ) : (
                            <CopyIcon className="w-3 h-3" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {copiedAnnual
                          ? t(
                              "admin.dataTableAdminUsers.tooltips.promoCodeCopied"
                            )
                          : t(
                              "admin.dataTableAdminUsers.tooltips.copyPromoCode"
                            )}
                      </TooltipContent>
                    </Tooltip>
                  </>
                )}
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: "Actions",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminUsers.columns.actions")}
        />
      ),
      cell: ({ row }) => {
        const user = row.original;
        return (
          <DataTableCellAdminUserActions user={user} onRefresh={onRefresh} />
        );
      },
    },
  ] as ColumnDef<AdminUserDTO>[];
};
